import { useState } from 'react';
import { IconHelp } from '@tabler/icons-react';
import { useToast } from '@hana/ui';
import { PF, PF_DIMS, PF_FIRST, PF_SUSPECTS, PF_VAT, pfRows, type PfDim } from './profit';
import { norm } from './payables';
import { f1 } from './view';
import s from './supplierPay.module.css';
import { useT } from '@hana/i18n';
import t from './profitTab.module.css';
import './finance.i18n';

const PER_LABEL = { m: '10/2026', q: 'Q4/2026', y: '2026' } as const;
const n0 = (x: number) => x.toLocaleString('vi-VN');
const COLS_SALE = [['Sale', 'l'], ['Số lô', 'r'], ['Doanh thu', 'r'], ['Lợi nhuận', 'r'], ['Biên', 'r'], ['% chỉ tiêu', 'r'], ['Bậc', 'r'], ['Hạng', 'r']] as const;

export function ProfitTab({ q, per }: { q: string; per: 'm' | 'q' | 'y' }) {
  const toast = useToast();
  const tx = useT();
  const [dim, setDim] = useState<PfDim>('sale');
  const sale = dim === 'sale';
  const all = pfRows(dim);
  const rows = q ? all.filter((r) => norm(`${r.k} ${r.sub}`).includes(norm(q))) : all;
  const cols = sale ? COLS_SALE : ([[PF_FIRST[dim], 'l'], ['Số lô', 'r'], ['Doanh thu', 'r'], ['Lợi nhuận', 'r'], ['Biên', 'r']] as const);
  return (
    <>
      <div className={t.tools}>
        <div className={s.seg} role="group" aria-label="Xem theo">
          {PF_DIMS.map(([k, l]) => <button key={k} type="button" aria-pressed={dim === k} onClick={() => setDim(k)}>{l}</button>)}
        </div>
        <span className={t.hint}>{tx('fn.pfHint')}</span>
      </div>
      <div className={t.grid}>
        <section className={s.card} aria-label="Chia lợi nhuận">
          <div className={`${t.row} ${t.head} ${sale ? t.sale : t.other}`}>
            {cols.map(([k, al]) => <span key={k} className={al === 'r' ? t.r : ''}>{k}</span>)}
          </div>
          {rows.map((r) => (
            <div key={r.k} className={`${t.row} ${sale ? t.sale : t.other}`}>
              <span className={t.name}><b>{r.k}</b><div>{r.sub}</div></span>
              <span className={t.r}>{r.n}</span>
              <span className={t.r}>{n0(r.rev)}</span>
              <b className={`${t.r} ${r.gp < 0 ? s.err : t.ok}`}>{f1(r.gp)}</b>
              <span className={`${t.r} ${s.tx2}`}>{f1(r.mg)}%</span>
              {sale && (
                <>
                  <span className={`${t.r} ${t.pct}`}>
                    <span className={t.bar}><i style={{ width: `${Math.min(r.pct, 100)}%`, background: r.pct >= 100 ? 'var(--d-green)' : r.pct >= 80 ? 'var(--d-amber)' : 'var(--badge)' }} /></span>
                    {r.pct}%
                  </span>
                  <span className={t.r}><span className={t.tier}>{r.tier}</span></span>
                  <b className={t.r}>#{r.rank}</b>
                </>
              )}
            </div>
          ))}
          <div className={t.foot}><span>{tx('fn.mTr')} · {tx('fn.pfFoot')}</span><span>{tx('fn.pfRows', { n: PF[dim].length, per: PER_LABEL[per] })}</span></div>
        </section>
        <aside className={t.side}>
          <section className={`${s.card} ${t.box}`}>
            <h2>{tx('fn.pfSus')}</h2>
            {PF_SUSPECTS.map((x) => (
              <div key={x.job} className={t.sus}>
                <div><a href={`/ezy/jobs/${x.job}`}>{x.job}</a><span className={`${t.why} ${t[x.tone]}`}>{x.why}</span></div>
                <div className={t.susk}>{x.k}</div>
              </div>
            ))}
          </section>
          <section className={`${s.card} ${t.box}`}>
            <h2>{tx('fn.pfVat')}</h2>
            {PF_VAT.map(([k, v, tone]) => <div key={k} className={t.vat}><span>{k}</span><b className={tone ? s.warn : ''}>{v}</b></div>)}
          </section>
          <button type="button" className={t.help} onClick={() => toast({ msg: 'Cách tính lợi nhuận chưa mở ở bản này' })}><IconHelp size={16} aria-hidden="true" />{tx('fn.pfHelp')}</button>
        </aside>
      </div>
    </>
  );
}
